import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Edit2, Save, X, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { buildApiUrl } from "@/lib/config";

interface ForexRate {
  id: number;
  currencyCode: string;
  rate: string;
  lastUpdated: string;
}

export default function ForexManagement() {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editRate, setEditRate] = useState("");
  const [newCurrency, setNewCurrency] = useState("");
  const [newRate, setNewRate] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch forex rates
  const { data: forexRates = [], isLoading } = useQuery<ForexRate[]>({
    queryKey: ["/api/forex-rates"],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl('api/forex-rates'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch forex rates');
      return response.json();
    },
  });

  // Update forex rate mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, rate }: { id: number; rate: string }) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`api/forex-rates/${id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rate })
      });
      if (!response.ok) throw new Error('Failed to update forex rate');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forex-rates"] });
      setEditingId(null);
      setEditRate("");
      toast({
        title: "Success",
        description: "Forex rate updated successfully"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Create forex rate mutation
  const createMutation = useMutation({
    mutationFn: async ({ currencyCode, rate }: { currencyCode: string; rate: string }) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl('api/forex-rates'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currencyCode, rate })
      });
      if (!response.ok) throw new Error('Failed to create forex rate');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forex-rates"] });
      setNewCurrency("");
      setNewRate("");
      toast({
        title: "Success",
        description: "New forex rate created successfully"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleEdit = (rate: ForexRate) => {
    setEditingId(rate.id);
    setEditRate(rate.rate);
  };

  const handleSave = (id: number) => {
    updateMutation.mutate({ id, rate: editRate });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditRate("");
  };

  const handleCreate = () => {
    if (newCurrency && newRate) {
      createMutation.mutate({ currencyCode: newCurrency.toUpperCase(), rate: newRate });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Forex Rates Management</CardTitle>
          <CardDescription>
            Manage and update currency exchange rates for the travel agency
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Add New Rate */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Add New Currency Rate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="currency">Currency Code</Label>
              <Input
                id="currency"
                placeholder="USD, EUR, GBP..."
                value={newCurrency}
                onChange={(e) => setNewCurrency(e.target.value)}
                className="uppercase"
              />
            </div>
            <div>
              <Label htmlFor="rate">Exchange Rate</Label>
              <Input
                id="rate"
                type="number"
                step="0.0001"
                placeholder="83.2450"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleCreate} disabled={!newCurrency || !newRate}>
                <Plus className="w-4 h-4 mr-2" />
                Add Rate
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Current Exchange Rates</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Currency</TableHead>
                <TableHead>Rate (INR)</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forexRates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">
                      {rate.currencyCode}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {editingId === rate.id ? (
                      <Input
                        type="number"
                        step="0.0001"
                        value={editRate}
                        onChange={(e) => setEditRate(e.target.value)}
                        className="w-32"
                      />
                    ) : (
                      <span className="font-mono">₹{rate.rate}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(rate.lastUpdated).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {editingId === rate.id ? (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleSave(rate.id)}
                          disabled={updateMutation.isPending}
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancel}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleEdit(rate)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {forexRates.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No forex rates available. Add your first currency rate above.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}